import React, { useCallback, useMemo, useState } from 'react';

import { Empty, Modal, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import ActionButton from '@src/components/Buttons/ActionButton';
import DisplayDurationWithSeconds from '@src/components/DisplayDurationWithSeconds';
import { DateUtil } from '@src/packages/utils/dates';

import DelayReasonModal from './DelayReasonModal';
import type { DelayRow } from './delayRows';
import { getUserLabelKey } from './getUserLabelKey';
import { useDelays } from './hooks/useDelays';
import styles from './Delays.module.less';

const EMPTY_VALUE = '—';

interface DelaysProps {
  divisionId?: string;
  executionId?: string;
  readOnly?: boolean;
}

const EmptyValue = () => <span className={styles.emptyValue}>{EMPTY_VALUE}</span>;

const Delays = ({ divisionId, executionId, readOnly = false }: DelaysProps) => {
  const {
    intl,
    delayRows,
    userNames,
    totalDurationSeconds,
    loading,
    isStalled,
    canReport,
    canEnd,
    submitting,
    reportDelay,
    endDelay,
  } = useDelays({ divisionId, executionId, readOnly });

  const [reportModalOpen, setReportModalOpen] = useState(false);

  const onReportConfirm = useCallback(
    async (reason: string) => {
      await reportDelay(reason);
      setReportModalOpen(false);
    },
    [reportDelay],
  );

  const onEndClick = useCallback(() => {
    Modal.confirm({
      centered: true,
      title: intl.formatMessage({ id: 'work.order.delays.end.confirm.title' }),
      content: intl.formatMessage({ id: 'work.order.delays.end.confirm.content' }),
      okText: intl.formatMessage({ id: 'work.order.delays.end' }),
      cancelText: intl.formatMessage({ id: 'button.cancel' }),
      onOk: () => endDelay(),
    });
  }, [endDelay, intl]);

  const renderDateTime = useCallback(
    (value: Date | null) => {
      if (!value) return <EmptyValue />;
      const { date, time } = DateUtil.formatDateTimeWithIntl(value, intl);

      return (
        <div className={styles.dateTime}>
          <span>{date}</span>
          <span className={styles.dateTimeTime}>{time}</span>
        </div>
      );
    },
    [intl],
  );

  const columns = useMemo<ColumnsType<DelayRow>>(
    () => [
      {
        title: intl.formatMessage({ id: 'work.order.delays.column.author' }),
        dataIndex: 'userId',
        key: 'author',
        width: 96,
        render: (userId: string | null) => {
          if (!userId) return <EmptyValue />;
          const userLabelKey = getUserLabelKey(userId);
          return userLabelKey
            ? intl.formatMessage({ id: userLabelKey })
            : (userNames[userId] ?? userId);
        },
      },
      {
        title: intl.formatMessage({ id: 'work.order.delays.column.reason' }),
        dataIndex: 'reason',
        key: 'reason',
        render: (reason: string | null) => reason ?? <EmptyValue />,
      },
      {
        title: intl.formatMessage({ id: 'work.order.delays.column.start' }),
        dataIndex: 'startedAt',
        key: 'startedAt',
        width: 116,
        render: (startedAt: Date) => renderDateTime(startedAt),
      },
      {
        title: intl.formatMessage({ id: 'work.order.delays.column.end' }),
        dataIndex: 'endedAt',
        key: 'endedAt',
        width: 116,
        render: (endedAt: Date | null) => renderDateTime(endedAt),
      },
      {
        title: intl.formatMessage({ id: 'work.order.delays.column.duration' }),
        dataIndex: 'durationSeconds',
        key: 'duration',
        width: 136,
        render: (durationSeconds: number) => (
          <DisplayDurationWithSeconds seconds={durationSeconds} />
        ),
      },
    ],
    [intl, renderDateTime, userNames],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.metrics}>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>
              {intl.formatMessage({ id: 'work.order.delays.total' })}
            </span>
            <span className={styles.metricValue}>{delayRows.length}</span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>
              {intl.formatMessage({ id: 'work.order.delays.totalDuration' })}
            </span>
            <span className={styles.metricValue}>
              <DisplayDurationWithSeconds seconds={totalDurationSeconds} />
            </span>
          </div>
          {isStalled && (
            <Tag className={styles.statusTag} color="processing">
              {intl.formatMessage({ id: 'work.order.delays.active' })}
            </Tag>
          )}
        </div>
        {!readOnly && (
          <div className={styles.actions}>
            <ActionButton
              data-testid="delay-end-action"
              icon="play_circle"
              label={intl.formatMessage({ id: 'work.order.delays.end' })}
              color="secondary"
              tooltipPosition="top"
              tooltipText={
                canEnd
                  ? intl.formatMessage({ id: 'work.order.delays.end' })
                  : intl.formatMessage({ id: 'work.order.delays.end.disabled' })
              }
              disabled={!canEnd || submitting}
              onClick={onEndClick}
            />
            <ActionButton
              data-testid="delay-report-action"
              icon="hourglass_top"
              label={intl.formatMessage({ id: 'work.order.delays.report' })}
              color="primary"
              tooltipPosition="top"
              tooltipText={
                canReport
                  ? intl.formatMessage({ id: 'work.order.delays.report' })
                  : intl.formatMessage({ id: 'work.order.delays.report.disabled' })
              }
              disabled={!canReport || submitting}
              onClick={() => setReportModalOpen(true)}
            />
          </div>
        )}
      </div>
      <Table
        rowKey="key"
        columns={columns}
        dataSource={delayRows}
        loading={loading}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({ id: 'work.order.delays.empty' })}
            />
          ),
        }}
      />
      {!readOnly && (
        <DelayReasonModal
          open={reportModalOpen}
          loading={submitting}
          onOk={reason => void onReportConfirm(reason)}
          onCancel={() => setReportModalOpen(false)}
        />
      )}
    </div>
  );
};

export default React.memo(Delays);
